import { DatePicker } from "@mui/x-date-pickers";
import moment from "moment";
import { cloneDeep } from "lodash";

import { Alert, Button, FormControl } from "@mui/material";
import { Grid, TextField } from "@mui/material";
import GridListItem from "components/common/grid/GridListItem";

import React, { useEffect } from "react";
import { useSelector } from "react-redux";

import formatDate from "utils/formatDate";

import usePatientModal from "components/pages/transmissions/modal/patient/usePatientModal";
import dayjs from "dayjs";
import SubmitButton from "components/Button/SubmitButton";
import { Formik } from "formik";
import * as Yup from "yup";
import { DATE_FORMAT } from "static/date";
import { getNumberInputValue } from "utils/number";
import { getFormattedDate } from "utils/date";

const validationSchema = Yup.object({
  last_send: Yup.date().nullable(),
  // .required("Last Send date is required"),
  next_send: Yup.date().nullable().required("Next Send date is required"),
  nextSendDays: Yup.number()
    .required("Next Send Cycle (Days) is required")
    .min(0, "Days cannot be negative"),
  total_sends: Yup.string().optional(),
  next_bill_date: Yup.date().nullable().required("Next Bill Date is required"),
  last_bill_date: Yup.date().nullable(),
});

const PatientTransmissionInfo = ({
  isEditMode,
  handleCancel,
  handleSubmit,
  setIsEditMode,
  isLoading,
  disabled,
}) => {
  const {
    newProfileInfo,
    isNextBillingDateUpdated,
    setIsNextBillingDateUpdated,
    handleNextBillDateUpdate,
    handleInputChange,
  } = usePatientModal();

  const { originalProfile } = useSelector((state) => state.transmission);

  useEffect(() => {
    if (!isEditMode) {
      setIsNextBillingDateUpdated(false);
    }
  }, [isEditMode]);

  return (
    <div className="patient-form__row patient-form__transmission">
      {isEditMode ? (
        <>
          <Formik
            initialValues={{
              last_send: newProfileInfo?.last_send,
              next_send: newProfileInfo?.next_send,
              nextSendDays: newProfileInfo?.nextSendDays,
              total_sends: newProfileInfo?.total_sends,
              next_bill_date: newProfileInfo?.next_bill_date,
              last_bill_date: newProfileInfo?.last_bill_date,
            }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ handleBlur, setFieldValue, errors, touched, handleSubmit }) => (
              <>
                <Grid container columnSpacing={2}>
                  <Grid item xs={3}>
                    <FormControl fullWidth margin="normal">
                      <DatePicker
                        inputVariant="outlined"
                        margin="normal"
                        fullWidth
                        autoOk
                        id="last_send"
                        label="Last Send"
                        name="last_send"
                        className="trans-table__filterDatePicker"
                        KeyboardButtonProps={{ "aria-label": "pick date" }}
                        format={DATE_FORMAT.DEFAULT}
                        value={
                          newProfileInfo?.last_send
                            ? dayjs(newProfileInfo?.last_send)
                            : null
                        }
                        onChange={(value) => {
                          const formattedDate = getFormattedDate(
                            value,
                            DATE_FORMAT.DEFAULT
                          );

                          const copy = cloneDeep(newProfileInfo);
                          copy.last_send = formattedDate;

                          handleInputChange(copy);
                          setFieldValue("last_send", formattedDate);
                        }}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error: touched.last_send && !!errors.last_send,
                            helperText: touched.last_send && errors.last_send,
                          },
                        }}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={3}>
                    <FormControl fullWidth margin="normal">
                      <DatePicker
                        margin="normal"
                        autoOk
                        inputVariant="outlined"
                        fullWidth
                        id="next_send"
                        name="next_send"
                        label="Next Send"
                        className="trans-table__filterDatePicker"
                        KeyboardButtonProps={{ "aria-label": "pick date" }}
                        format={DATE_FORMAT.DEFAULT}
                        value={
                          newProfileInfo.next_send
                            ? dayjs(newProfileInfo.next_send)
                            : null
                        }
                        onChange={(value) => {
                          const formattedDate = getFormattedDate(
                            value,
                            DATE_FORMAT.DEFAULT
                          );

                          const copy = cloneDeep(newProfileInfo);
                          copy.next_send = formattedDate;
                          copy.next_bill_date = originalProfile?.next_bill_date;

                          handleInputChange(copy);
                          setIsNextBillingDateUpdated(false);
                          setFieldValue("next_send", formattedDate);
                        }}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error: touched.next_send && !!errors.next_send,
                            helperText: touched.next_send && errors.next_send,
                          },
                        }}
                      />
                    </FormControl>
                  </Grid>

                  <Grid item xs={3}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      type="number"
                      name="nextSendDays"
                      required
                      value={newProfileInfo?.nextSendDays}
                      label="Next Send Cycle (Days)"
                      onChange={(event) => {
                        const value = event.target.value;
                        const copy = cloneDeep(newProfileInfo);
                        copy.nextSendDays = getNumberInputValue(value) || "0";

                        handleInputChange(copy);
                        setFieldValue("nextSendDays", value);
                      }}
                      onBlur={handleBlur}
                      error={touched.nextSendDays && !!errors.nextSendDays}
                      helperText={touched.nextSendDays && errors.nextSendDays}
                    />
                  </Grid>

                  {isNextBillingDateUpdated && (
                    <Grid item xs={12}>
                      <Alert
                        severity="warning"
                        className="next-send-warning mt-3 mb-3"
                      >
                        Next Send date auto-adjusted to{" "}
                        <strong>
                          {moment(newProfileInfo?.next_send).format(
                            DATE_FORMAT.DEFAULT
                          )}
                        </strong>
                      </Alert>
                    </Grid>
                  )}

                  <Grid item xs={3}>
                    <TextField
                      fullWidth
                      margin="normal"
                      variant="outlined"
                      value={newProfileInfo?.total_sends}
                      label="Total Sends"
                      name="total_sends"
                      onChange={(event) => {
                        const value = event.target.value;

                        const copy = cloneDeep(newProfileInfo);
                        copy.total_sends = value;

                        handleInputChange(copy);
                        setFieldValue("total_sends", value);
                      }}
                      onBlur={handleBlur}
                      error={touched.total_sends && !!errors.total_sends}
                      helperText={touched.total_sends && errors.total_sends}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      disabled
                      fullWidth
                      margin="normal"
                      variant="outlined"
                      name="last_bill_date"
                      label="Last Bill Date"
                      value={
                        newProfileInfo?.last_bill_date
                          ? formatDate(newProfileInfo?.last_bill_date)
                          : ""
                      }
                      error={touched.last_bill_date && !!errors.last_bill_date}
                      helperText={
                        touched.last_bill_date && errors.last_bill_date
                      }
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <FormControl fullWidth margin="normal">
                      <DatePicker
                        margin="normal"
                        autoOk
                        inputVariant="outlined"
                        fullWidth
                        id="next_bill_date"
                        label="Next Bill Date"
                        className="trans-table__filterDatePicker"
                        name="next_bill_date"
                        format={DATE_FORMAT.DEFAULT}
                        KeyboardButtonProps={{ "aria-label": "pick date" }}
                        value={
                          newProfileInfo.next_bill_date
                            ? dayjs(newProfileInfo.next_bill_date)
                            : null
                        }
                        onChange={(value) => {
                          const formattedDate = getFormattedDate(
                            value,
                            DATE_FORMAT.DEFAULT
                          );

                          handleNextBillDateUpdate(formattedDate);
                          setFieldValue("next_bill_date", formattedDate);
                        }}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error:
                              touched.next_bill_date && !!errors.next_bill_date,
                            helperText:
                              touched.next_bill_date && errors.next_bill_date,
                          },
                        }}
                      />
                    </FormControl>
                  </Grid>

                  <Grid xs={12}>
                    <Alert
                      severity="warning"
                      className="next-send-warning mt-3 mb-3"
                    >
                      Updating the Next Bill Date will auto adjust the Next Send
                      Date.
                    </Alert>
                  </Grid>
                </Grid>
                <div className="patient-form__action">
                  <Button
                    onClick={() => {
                      handleCancel();
                      setIsEditMode(false);
                    }}
                    variant="outlined"
                    color="secondary"
                  >
                    Cancel
                  </Button>
                  <SubmitButton
                    handleClick={handleSubmit}
                    variant="contained"
                    color="secondary"
                    isLoading={isLoading}
                    disabled={disabled}
                  />
                </div>
              </>
            )}
          </Formik>
        </>
      ) : (
        <Grid container columnSpacing={2} className="view-pdf__drawer-grid">
          <Grid item container>
            <GridListItem
              title="LAST SEND"
              subTitle={
                newProfileInfo.last_send
                  ? formatDate(newProfileInfo.last_send)
                  : null
              }
              size="3"
              isDate
            />
            <GridListItem
              title="NEXT SEND"
              subTitle={
                newProfileInfo.next_send
                  ? formatDate(newProfileInfo.next_send)
                  : null
              }
              size="3"
              isDate
            />
            <GridListItem
              title="NEXT SEND CYCLE (DAYS)"
              subTitle={newProfileInfo.nextSendDays}
              size="3"
            />
            <GridListItem
              title="LAST BILL DATE"
              subTitle={
                newProfileInfo.last_bill_date
                  ? formatDate(newProfileInfo.last_bill_date)
                  : null
              }
              size="3"
              isDate
            />
            <GridListItem
              title="NEXT BILL DATE"
              subTitle={
                newProfileInfo.next_bill_date
                  ? formatDate(newProfileInfo.next_bill_date)
                  : null
              }
              size="3"
              isDate
            />
            <GridListItem
              title="TOTAL SENDS"
              subTitle={newProfileInfo.total_sends ?? "—"}
              size="3"
            />
          </Grid>
        </Grid>
      )}
    </div>
  );
};

export default PatientTransmissionInfo;
